<template>
  <div>
    <div>
      <v-app>
        <v-main>
          <template>
            <router-view></router-view>
            <toasts />
          </template>
        </v-main>
      </v-app>
    </div>
  </div>
</template>

<script>
// import StatusTable from "./components/StatusTable";
import { Auth } from "aws-amplify"; // Import AWS Amplify Auth module
import { Hub } from "aws-amplify";
import { navigateTo } from "./router";
import Toasts from "./components/common/Toasts.vue";
import { mapState } from "vuex";
import { appendSubscriptionEventLog } from "./utils/log";
import {
  getPayPalAccessToken,
  fetchPayPalSubscription as fetchPayPalSubscriptionApi,
  parseBillingInfo,
} from "./utils/subscription";

export default {
  name: "App",
  data() {
    return {};
  },
  components: {
    // StatusTable,
    Toasts,
  },
  beforeCreate() {
    const signIn = async (user, sourceEvent = "signIn") => {
      const attrs = user.attributes || {};
      const subscriptionID = attrs["custom:subscriptionID"] || "";
      const subStatus = attrs["custom:pro"] || "inactive";
      const lastPayment = attrs["custom:lastPayment"] || "";

      await this.$store.dispatch("setAuthState", {
        state: "signed-in",
        user: {
          username: user.username,
          email: attrs.email,
          email_verified: attrs.email_verified,
          phone_number: attrs.phone_number,
          phone_number_verified: attrs.phone_number_verified,
          subID: subscriptionID,
          subStatus,
          lastPayment,
        },
      });
      console.log("user sign in", attrs);
      this.$store.dispatch("setSubStatus", subStatus);
      this.$store.dispatch("setSubID", subscriptionID);
      if (lastPayment) {
        this.$store.dispatch("setLastPayment", lastPayment);
      }

      await this.refreshSubscriptionFromAws();
      if (this.subscriptionID) {
        await this.syncPayPalBillingInfo();
      }

      const validUntil = this.$store.getters.getNextBillingtime || "unknown";
      this.$store.dispatch("addToast", {
        severity: "success",
        life: 3000,
        summary: "Singed in",
        detail: "Successfully singed in",
      });
      if (sourceEvent === "signIn") {
        appendSubscriptionEventLog({
          eventType: "sign-in",
          validUntil,
          status: subStatus || "unknown",
          subscriptionId: this.subscriptionID || "none",
          lastPayment: this.$store.getters.getLastPayment || "unknown",
        });
      }
    };

    const signOut = async () => {
      await this.$store.dispatch("setAuthState", {
        state: "signed-out",
        user: null,
      });
      this.$store.dispatch("addToast", {
        severity: "success",
        life: 3000,
        summary: "Singed out",
        detail: "Successfully singed out",
      });
    };

    this.cancelListener = Hub.listen("auth", async (data) => {
      const { payload } = data;

      if (payload.event == "signIn" || payload.event === "signUp") {
        await signIn(payload.data, payload.event);
        navigateTo({ path: "/" });
      } else if (payload.event == "signOut") {
        console.log("sign out in hub ");
        await signOut();
        navigateTo({ path: "/" });
      }
    });
  },
  computed: {
    ...mapState(["statusTable"]),
    isToastVisible() {
      return this.statusTable?.showToast;
    },
    subscriptionID() {
      return this.statusTable.authUser?.subID;
    },
    nextBillingTime() {
      return this.$store.getters.getNextBillingtime;
    },
  },
  watch: {
    subscriptionID(newValue, oldValue) {
      if (newValue) {
        setTimeout(async () => {
          await this.syncPayPalBillingInfo();
        }, 360000);
      }
    },
  },
  methods: {
    async refreshSubscriptionFromAws() {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const attrs = user.attributes || {};
        const subscriptionID = attrs["custom:subscriptionID"] || "";
        const subStatus = attrs["custom:pro"] || "inactive";
        const lastPayment = attrs["custom:lastPayment"] || "";

        this.$store.dispatch("setSubID", subscriptionID);
        this.$store.dispatch("setSubStatus", subStatus);
        if (lastPayment) {
          this.$store.dispatch("setLastPayment", lastPayment);
        }

        const authUser = this.$store.getters.getAuthData;
        if (authUser) {
          this.$store.dispatch("setAuthState", {
            state: this.$store.getters.getAuth || "signed-in",
            user: { ...authUser, subID: subscriptionID, subStatus: subStatus, lastPayment },
          });
        }

        return attrs;
      } catch (error) {
        if (error === "not authenticated" || error?.message?.toLowerCase().includes("not authenticated")) {
          return null;
        }
        console.error("Error refreshing subscription status from AWS:", error);
        return null;
      }
    },
    async syncPayPalBillingInfo() {
      if (!this.subscriptionID || this.$store.getters.getAuth !== "signed-in") return;

      try {
        const access_token = await getPayPalAccessToken();
        if (!access_token) throw new Error("PayPal access token not received");
        const data = await fetchPayPalSubscriptionApi(access_token, this.subscriptionID);
        if (!data) {
          return null;
        }
        const { nextBilling, lastPayment, status, nextBillingRaw } = parseBillingInfo(data);
        try {
          const user = await Auth.currentAuthenticatedUser();
          if (lastPayment && lastPayment !== "unknown") {
            await Auth.updateUserAttributes(user, { "custom:lastPayment": lastPayment });
            this.$store.dispatch("setLastPayment", lastPayment);
          }
          if (nextBilling === "unknown") {
            this.$store.dispatch("setNextBillingTime", { nextBillingTime: "unknown" });
          } else if (nextBilling) {
            this.$store.dispatch("setNextBillingTime", { nextBillingTime: nextBillingRaw || nextBilling });
          }
        } catch (error) {
          console.error("Failed to update AWS user attributes with PayPal data:", error);
        }

        return data;
      } catch (error) {
        console.error("Error fetching PayPal billing info:", error);
        return null;
      }
    },
    async fetchPayPalSubscription(accessToken, subscriptionID) {
      console.warn("fetchPayPalSubscription is deprecated; use utils/subscription instead.");
      return fetchPayPalSubscriptionApi(accessToken, subscriptionID);
    },
  },

  beforeUnmount() {
    this.cancelListener();
  },
  created() {
    document.title = "Earned Value";
  },
  async mounted() {
    await this.refreshSubscriptionFromAws();
    if (this.subscriptionID) {
      await this.syncPayPalBillingInfo();
    }
    setInterval(async () => {
      await this.refreshSubscriptionFromAws();
      await this.syncPayPalBillingInfo(); // Check every 24 hours
    }, 86400000); // 24 hours in milliseconds
  },
};
</script>

<style>
.sc-amplify-sign-in-button {
  display: none !important;
}

button.amplify-sign-in-with-aws {
  display: none !important;
}
</style>
